/**
 * ListSelection component.
 *
 * Renders a scrollable list of clickable items for specialist/appointment
 * selection. Each item shows a title, optional subtitle, optional icon,
 * and a selection indicator. Single-select behavior: clicking an item
 * emits the selected ID and visually highlights the chosen item.
 */

import type { ChatMessage, ListItem } from "../../types";

export interface ListSelectionOptions {
  message: ChatMessage;
  onSelect: (itemId: string) => void;
}

export class ListSelection {
  private readonly element: HTMLElement;
  private selectedId: string | null = null;

  constructor(options: ListSelectionOptions) {
    const { message, onSelect } = options;
    const items = message.listItems ?? [];

    this.element = document.createElement("div");
    this.element.className = "hana-list";
    this.element.setAttribute("data-message-id", message.id);
    this.element.setAttribute("role", "listbox");
    this.element.setAttribute("aria-label", "Select an option");

    // Optional header text above the list
    if (message.content) {
      const header = document.createElement("div");
      header.className = "hana-list__header";
      header.textContent = message.content;
      this.element.appendChild(header);
    }

    // Scrollable container for items
    const listContainer = document.createElement("div");
    listContainer.className = "hana-list__items";

    // Make it scrollable if more than 5 items
    if (items.length > 5) {
      listContainer.classList.add("hana-list__items--scrollable");
    }

    items.forEach((item: ListItem) => {
      const itemEl = this.createItemElement(item, onSelect);
      listContainer.appendChild(itemEl);
    });

    this.element.appendChild(listContainer);
  }

  getElement(): HTMLElement {
    return this.element;
  }

  getSelectedId(): string | null {
    return this.selectedId;
  }

  private createItemElement(
    item: ListItem,
    onSelect: (itemId: string) => void,
  ): HTMLElement {
    const itemEl = document.createElement("button");
    itemEl.className = "hana-list__item";
    itemEl.setAttribute("role", "option");
    itemEl.setAttribute("aria-selected", "false");
    itemEl.setAttribute("data-item-id", item.id);
    itemEl.setAttribute("type", "button");

    // Icon/avatar (optional)
    if (item.iconUrl) {
      const icon = document.createElement("img");
      icon.className = "hana-list__icon";
      icon.src = item.iconUrl;
      icon.alt = "";
      icon.setAttribute("aria-hidden", "true");
      icon.addEventListener("error", () => {
        icon.style.display = "none";
      });
      itemEl.appendChild(icon);
    }

    // Text container
    const textEl = document.createElement("div");
    textEl.className = "hana-list__text";

    const titleEl = document.createElement("span");
    titleEl.className = "hana-list__title";
    titleEl.textContent = item.title;
    textEl.appendChild(titleEl);

    if (item.subtitle) {
      const subtitleEl = document.createElement("span");
      subtitleEl.className = "hana-list__subtitle";
      subtitleEl.textContent = item.subtitle;
      textEl.appendChild(subtitleEl);
    }

    itemEl.appendChild(textEl);

    // Selection indicator
    const indicator = document.createElement("span");
    indicator.className = "hana-list__indicator";
    indicator.setAttribute("aria-hidden", "true");
    itemEl.appendChild(indicator);

    // Click handler
    itemEl.addEventListener("click", () => {
      if (this.selectedId !== null) return; // Single select, no re-selection

      this.selectedId = item.id;

      // Update visual state for all items
      const allItems = this.element.querySelectorAll(".hana-list__item");
      allItems.forEach((el) => {
        el.setAttribute("aria-selected", "false");
        el.classList.remove("hana-list__item--selected");
        (el as HTMLButtonElement).disabled = true;
      });

      itemEl.setAttribute("aria-selected", "true");
      itemEl.classList.add("hana-list__item--selected");

      onSelect(item.id);
    });

    return itemEl;
  }
}
